import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { TitleSidebar } from "./TitleDashboardPage";
import {
  Plus,
  Search,
  Filter,
  ChevronRight,
  X,
  Building2,
  User,
  DollarSign,
  Calendar,
  FileText,
  MoreVertical
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Stage definitions
const STAGES = [
  { key: "order_received", label: "Order Received", color: "blue" },
  { key: "title_search", label: "Title Search", color: "purple" },
  { key: "search_review", label: "Search Review", color: "amber" },
  { key: "commitment_issued", label: "Commitment Issued", color: "cyan" },
  { key: "requirements_pending", label: "Requirements Pending", color: "orange" },
  { key: "clear_to_close", label: "Clear to Close", color: "green" },
  { key: "closing_scheduled", label: "Closing Scheduled", color: "emerald" },
  { key: "closing_complete", label: "Closing Complete", color: "teal" },
  { key: "policy_issued", label: "Policy Issued", color: "zinc" }
];

const STAGE_COLORS = {
  order_received: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
  title_search: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30",
  search_review: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30",
  commitment_issued: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  requirements_pending: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  clear_to_close: "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-green-500/30",
  closing_scheduled: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
  closing_complete: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  policy_issued: "bg-white/20/20 text-white/50 border-white/[0.15]/30"
};

function StageBadge({ stage }) {
  const stageInfo = STAGES.find(s => s.key === stage);
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${STAGE_COLORS[stage] || "bg-white/[0.08] text-white/70 border-white/[0.1]"}`}>
      {stageInfo?.label || stage}
    </span>
  );
}

// New Order Modal
function NewOrderModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_address: "",
    buyer_name: "",
    buyer_email: "",
    seller_name: "",
    seller_email: "",
    sale_price: "",
    loan_amount: "",
    lender_name: "",
    realtor_name: "",
    realtor_email: "",
    expected_close_date: "",
    order_type: "purchase",
    notes: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        sale_price: parseFloat(formData.sale_price) || 0,
        loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null
      };

      await axios.post(`${BACKEND_URL}/api/title/orders`, payload, { withCredentials: true });
      toast.success("Order created successfully");
      onSuccess();
      onClose();
      setFormData({
        property_address: "",
        buyer_name: "",
        buyer_email: "",
        seller_name: "",
        seller_email: "",
        sale_price: "",
        loan_amount: "",
        lender_name: "",
        realtor_name: "",
        realtor_email: "",
        expected_close_date: "",
        order_type: "purchase",
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/[0.08] flex items-center justify-between sticky top-0 bg-[#0A0A0A]/80 backdrop-blur-sm z-10">
          <h2 className="font-heading text-xl font-bold text-white uppercase">New Title Order</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-modal-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Property Info */}
          <div>
            <h3 className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Property Information
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-white/70">Property Address *</Label>
                <Input
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  required
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="123 Main St, Dallas, TX 75201"
                  data-testid="order-property-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Sale Price *</Label>
                  <Input
                    name="sale_price"
                    type="number"
                    value={formData.sale_price}
                    onChange={handleChange}
                    required
                    className="bg-black border-white/[0.08] text-white mt-1"
                    placeholder="250000"
                    data-testid="order-sale-price"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Order Type</Label>
                  <select
                    name="order_type"
                    value={formData.order_type}
                    onChange={handleChange}
                    className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 mt-1"
                    data-testid="order-type-select"
                  >
                    <option value="purchase">Purchase</option>
                    <option value="refinance">Refinance</option>
                    <option value="sale">Sale</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Buyer Info */}
          <div>
            <h3 className="text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] font-medium mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Buyer Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Buyer Name *</Label>
                <Input
                  name="buyer_name"
                  value={formData.buyer_name}
                  onChange={handleChange}
                  required
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="John Doe"
                  data-testid="order-buyer-name"
                />
              </div>
              <div>
                <Label className="text-white/70">Buyer Email</Label>
                <Input
                  name="buyer_email"
                  type="email"
                  value={formData.buyer_email}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="buyer@email.com"
                  data-testid="order-buyer-email"
                />
              </div>
            </div>
          </div>

          {/* Seller Info */}
          <div>
            <h3 className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] font-medium mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Seller Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Seller Name *</Label>
                <Input
                  name="seller_name"
                  value={formData.seller_name}
                  onChange={handleChange}
                  required
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="Jane Smith"
                  data-testid="order-seller-name"
                />
              </div>
              <div>
                <Label className="text-white/70">Seller Email</Label>
                <Input
                  name="seller_email"
                  type="email"
                  value={formData.seller_email}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="seller@email.com"
                  data-testid="order-seller-email"
                />
              </div>
            </div>
          </div>

          {/* Financing Info */}
          <div>
            <h3 className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> Financing Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Loan Amount</Label>
                <Input
                  name="loan_amount"
                  type="number"
                  value={formData.loan_amount}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="200000"
                  data-testid="order-loan-amount"
                />
              </div>
              <div>
                <Label className="text-white/70">Lender Name</Label>
                <Input
                  name="lender_name"
                  value={formData.lender_name}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="ABC Mortgage"
                  data-testid="order-lender-name"
                />
              </div>
            </div>
          </div>

          {/* Realtor & Closing */}
          <div>
            <h3 className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] font-medium mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Additional Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Realtor Name</Label>
                <Input
                  name="realtor_name"
                  value={formData.realtor_name}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="Mike Johnson"
                  data-testid="order-realtor-name"
                />
              </div>
              <div>
                <Label className="text-white/70">Expected Close Date</Label>
                <Input
                  name="expected_close_date"
                  type="date"
                  value={formData.expected_close_date}
                  onChange={handleChange}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  data-testid="order-close-date"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-white/70">Notes</Label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 mt-1 min-h-[80px]"
                placeholder="Any additional notes..."
                data-testid="order-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-none"
              data-testid="create-order-btn"
            >
              {loading ? "Creating..." : "Create Order"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TitleOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStage, setSelectedStage] = useState(searchParams.get("stage") || "all");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchQuery, selectedStage]);

  const fetchData = async () => {
    try {
      const [profileRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/title/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/title/orders`, { withCredentials: true })
      ]);

      setCompany(profileRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/title");
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.property_address?.toLowerCase().includes(query) ||
          o.buyer_name?.toLowerCase().includes(query) ||
          o.seller_name?.toLowerCase().includes(query)
      );
    }

    if (selectedStage !== "all") {
      filtered = filtered.filter((o) => o.stage === selectedStage);
    }

    setFilteredOrders(filtered);
  };

  const handleStageFilter = (stage) => {
    setSelectedStage(stage);
    if (stage === "all") {
      searchParams.delete("stage");
    } else {
      searchParams.set("stage", stage);
    }
    setSearchParams(searchParams);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/title/auth/logout`, {}, { withCredentials: true });
      navigate("/title");
    } catch (error) {
      navigate("/title");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="title-orders-page">
      <TitleSidebar company={company} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Title Orders</h1>
            <p className="text-white/50 mt-1">{orders.length} total orders</p>
          </div>
          <Button
            onClick={() => setShowNewModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-none"
            data-testid="new-order-header-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> New Order
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <Input
                  placeholder="Search by address, buyer, or seller..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-black border-white/[0.08] text-white pl-10"
                  data-testid="orders-search-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-white/50" />
              <select
                value={selectedStage}
                onChange={(e) => handleStageFilter(e.target.value)}
                className="bg-black border border-white/[0.08] text-white px-3 py-2 text-sm"
                data-testid="stage-filter-select"
              >
                <option value="all">All Stages</option>
                {STAGES.map((stage) => (
                  <option key={stage.key} value={stage.key}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/[0.08] text-white/50 text-sm font-medium">
            <div className="col-span-4">Property</div>
            <div className="col-span-2">Buyer/Seller</div>
            <div className="col-span-2">Sale Price</div>
            <div className="col-span-2">Stage</div>
            <div className="col-span-2">Created</div>
          </div>

          {/* Orders */}
          {filteredOrders.length > 0 ? (
            <div className="divide-y divide-white/[0.06]">
              {filteredOrders.map((order) => (
                <Link
                  key={order.order_id}
                  to={`/title/orders/${order.order_id}`}
                  className="grid grid-cols-12 gap-4 p-4 hover:bg-white/[0.06]/50 transition-all duration-300 items-center"
                  data-testid={`order-row-${order.order_id}`}
                >
                  <div className="col-span-4">
                    <p className="text-white font-medium truncate">{order.property_address}</p>
                    <p className="text-white/40 text-xs capitalize">{order.order_type}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-white text-sm truncate">{order.buyer_name}</p>
                    <p className="text-white/50 text-xs truncate">{order.seller_name}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono">{formatCurrency(order.sale_price)}</p>
                  </div>
                  <div className="col-span-2">
                    <StageBadge stage={order.stage} />
                  </div>
                  <div className="col-span-2 flex items-center justify-between">
                    <span className="text-white/50 text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">
                {searchQuery || selectedStage !== "all"
                  ? "No orders match your filters"
                  : "No orders yet"}
              </p>
              {!searchQuery && selectedStage === "all" && (
                <Button
                  onClick={() => setShowNewModal(true)}
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                >
                  Create First Order
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      <NewOrderModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={fetchData}
      />
    </div>
  );
}
